import { alreadyHasPhotos, claimRequest, settleRequest, unsureSaveWords } from '../client-ref.js';
import { useEffect, useState } from 'react';
import { api } from '../api.js';
import { useConfirm } from './ConfirmDialog.jsx';
import { NotePhotoPicker } from './NotePhotoPicker.jsx';
import { uploadNotePhotos, photosFailedMessage } from '../note-photos.js';
import { localDateIso } from '../local-date.js';
import { TEST_LABEL } from '../../../shared/treatment.js';

// A resident's own notes, newest first, with a box to add one. The date
// defaults to today and can be set back, because a keeper filling in a
// coral they have had for years is writing down things that already
// happened. `base` is the resident's API path (tank or account).

function noteDate(ts) {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

const COURSE_ENTRY_LABEL = {
  observation: 'Observation',
  dose: 'Dose',
  test: 'Test',
  water_change: 'Water change',
};

const localToday = () => localDateIso();

// A photo added with a note is one of the resident's photos too, so the
// caller's gallery is told to refresh (onPhotosChanged).
export function ResidentNotes({
  base,
  canWrite = true,
  isOwner = false,
  addedAt = null,
  disabled = false,
  onPhotosChanged,
}) {
  const confirm = useConfirm();
  const [notes, setNotes] = useState(null);
  const [courseEntries, setCourseEntries] = useState([]);
  const [draft, setDraft] = useState('');
  const [photos, setPhotos] = useState([]);
  const [day, setDay] = useState(localToday);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    let alive = true;
    setNotes(null);
    api(`${base}/notes`)
      .then((list) => alive && setNotes(list || []))
      .catch((e) => {
        if (!alive) return;
        setNotes([]);
        setErr(e.message || 'Could not load the notes.');
      });
    // Its quarantine and treatment log sits in the same dated list, so the
    // journey reads straight through the weeks in quarantine. Those entries
    // are edited on the course, not here.
    setCourseEntries([]);
    api(`${base}/treatments`)
      .then((list) => alive && setCourseEntries(Array.isArray(list) ? list : []))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [base]);

  const entryText = (e) => {
    const amount =
      e.amount != null ? `${Number(e.amount).toString()}${e.unit ? ` ${e.unit}` : ''}` : '';
    const test = e.kind === 'test' && e.test ? TEST_LABEL[e.test] : '';
    return [test, amount, e.note].filter(Boolean).join(' · ');
  };
  const merged = [
    ...(notes || []).map((n) => ({ type: 'note', ts: n.ts, n })),
    ...courseEntries.map((e) => ({ type: 'course', ts: e.ts, e })),
  ].sort((a, b) => new Date(b.ts).getTime() - new Date(a.ts).getTime());

  const load = async () => {
    const list = await api(`${base}/notes`);
    setNotes(list || []);
  };

  const save = async () => {
    const body = draft.trim();
    if ((!body && !photos.length) || busy) return;
    setBusy(true);
    setErr('');
    let posted = false;
    try {
      const sendDay = day === localToday() ? null : day;
      const scope = `resident-note:${base}`;
      const row = await api(`${base}/notes`, {
        method: 'POST',
        body: {
          body,
          day: sendDay,
          with_photos: photos.length > 0,
          client_ref: claimRequest(scope, JSON.stringify([body, sendDay, photos.length])),
        },
      });
      posted = true;
      settleRequest(scope);
      const photoErrors = [];
      // The same note again (its reply was lost): its photos went already.
      const failed = alreadyHasPhotos(row, photos.length)
        ? 0
        : await uploadNotePhotos(`${base}/image?note=${row.id}`, photos, photoErrors);
      if (!body && failed === photos.length) {
        await api(`${base}/notes/${row.id}`, { method: 'DELETE' }).catch(() => {});
        setErr(photoErrors[0] || 'The photo could not be uploaded. Try again.');
      } else {
        setDraft('');
        setPhotos([]);
        setDay(localToday());
        setErr(photosFailedMessage(failed, photos.length, photoErrors[0]));
      }
      await load();
      if (photos.length > failed) await onPhotosChanged?.();
    } catch (e) {
      setErr((!posted && unsureSaveWords(e)) || e.message || 'Could not save the note.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (n) => {
    const hadPhotos = n.image_ids?.length > 0;
    const ok = await confirm({
      title: 'Delete this note?',
      body: hadPhotos ? 'Its photos are deleted too.' : undefined,
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try {
      await api(`${base}/notes/${n.id}`, { method: 'DELETE' });
      setNotes((prev) => (prev || []).filter((x) => x.id !== n.id));
      if (hadPhotos) await onPhotosChanged?.();
    } catch (e) {
      setErr(e.message || 'Could not delete the note.');
    }
  };

  // A course whose tank was deleted is reachable only from here, so its
  // owner can delete it here (the server checks the same).
  const orphanCourses = isOwner ? orphanedCourses(courseEntries) : [];
  const residentId = String(base).split('/').pop();
  const removeCourse = async (c) => {
    const ok = await confirm({
      title: `Remove "${c.title}" from this record?`,
      body: 'Other residents that were in it keep their log. If none are left, the log and its photos are deleted. The tank it was kept in is already deleted.',
      confirmLabel: 'Remove',
      destructive: true,
    });
    if (!ok) return;
    try {
      await api(`/api/livestock/${residentId}/treatments/${c.id}`, { method: 'DELETE' });
      setCourseEntries((prev) => prev.filter((e) => e.course_id !== c.id));
    } catch (e) {
      setErr(e.message || 'Could not delete it.');
    }
  };

  const minDay = addedAt ? String(addedAt).slice(0, 10) : undefined;

  return (
    <div className="res-notes">
      <div className="hv-resident-gallery-eyebrow font-heading">Notes</div>
      {canWrite && (
        <div className="res-notes-composer">
          <textarea
            rows={2}
            maxLength={1000}
            placeholder="What you saw or did with it. Moved it, fed it, it closed up…"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            disabled={busy || disabled}
            aria-label="New note"
          />
          <NotePhotoPicker files={photos} onChange={setPhotos} disabled={busy || disabled} />
          <div className="res-notes-row">
            <input
              type="date"
              value={day}
              min={minDay}
              max={localToday()}
              onChange={(e) => setDay(e.target.value || localToday())}
              disabled={busy || disabled}
              aria-label="Date of the note"
            />
            <button
              type="button"
              className="res-notes-save font-heading"
              onClick={save}
              disabled={busy || disabled || (!draft.trim() && !photos.length)}
            >
              {busy ? 'Saving…' : 'Add note'}
            </button>
          </div>
        </div>
      )}
      {notes === null ? null : merged.length === 0 ? (
        <p className="lh-empty">
          {canWrite
            ? 'No notes yet. They stay with it, even after it leaves the tank.'
            : 'No notes.'}
        </p>
      ) : (
        <ul className="res-notes-list">
          {merged.map((m) => {
            if (m.type === 'course') {
              return (
                <li key={`c-${m.e.id}`}>
                  <span className="res-notes-date font-mono">{noteDate(m.e.ts)}</span>
                  <span className="res-notes-body">
                    <span className="res-notes-course font-heading">
                      {m.e.course_kind === 'quarantine' ? 'Quarantine' : 'Treatment'}:{' '}
                      {m.e.course_title} · {COURSE_ENTRY_LABEL[m.e.kind] || m.e.kind}
                    </span>
                    {entryText(m.e) && <> {entryText(m.e)}</>}
                    {m.e.image_ids?.length > 0 && (
                      <span className="hv-journal-photos">
                        {m.e.image_ids.map((id) => (
                          <a
                            key={id}
                            href={`${base}/treatment-image/${id}`}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <img src={`${base}/treatment-image/${id}`} alt="" loading="lazy" />
                          </a>
                        ))}
                      </span>
                    )}
                  </span>
                </li>
              );
            }
            const n = m.n;
            return (
              <li key={n.id}>
                <span className="res-notes-date font-mono">{noteDate(n.ts)}</span>
                <span className="res-notes-body">
                  {n.body}
                  {n.image_ids?.length > 0 && (
                    <span className="hv-journal-photos">
                      {n.image_ids.map((id) => (
                        <a key={id} href={`${base}/image/${id}`} target="_blank" rel="noreferrer">
                          <img src={`${base}/image/${id}`} alt="" loading="lazy" />
                        </a>
                      ))}
                    </span>
                  )}
                </span>
                {canWrite && (
                  <button
                    type="button"
                    className="hv-journal-x"
                    onClick={() => remove(n)}
                    aria-label="Delete this note"
                  >
                    ×
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {orphanCourses.map((c) => (
        <button
          key={c.id}
          type="button"
          className="rj-mistake res-notes-course-delete"
          onClick={() => removeCourse(c)}
          disabled={busy || disabled}
        >
          Delete this {c.kind === 'quarantine' ? 'quarantine' : 'treatment'} log: {c.title}
        </button>
      ))}
      {err && (
        <p className="hv-resident-err" role="alert">
          {err}
        </p>
      )}
    </div>
  );
}

// One per course, for the entries whose course lost its tank.
export function orphanedCourses(entries) {
  const seen = new Map();
  for (const e of entries || []) {
    if (!e.course_orphaned || seen.has(e.course_id)) continue;
    seen.set(e.course_id, { id: e.course_id, title: e.course_title, kind: e.course_kind });
  }
  return [...seen.values()];
}
